<template>
  <div
    :class="[
      'overflow-x-hidden min-h-screen flex flex-col',
      shouldShowLayout ? 'bg-gray-50' : ''
    ]"
  >
    <!-- Top Bar - Fixed header with logo, market status, and user actions -->
    <nav v-if="authStore.isAuthenticated && shouldShowLayout" class="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 sm:px-6 h-16 shadow-sm">
      <div class="h-full flex items-center justify-between">
        <!-- Left: Logo + Mobile Menu Button -->
        <div class="flex items-center space-x-4">
          <!-- Mobile Menu Button -->
          <button
            @click="sidebarStore.toggleMobileMenu()"
            class="lg:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>

          <!-- Logo -->
          <RouterLink to="/dashboard" class="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <img src="/InvestBuddyLogo.png" alt="InvestBuddy.ai" class="w-8 h-8 sm:w-10 sm:h-10 rounded-full" />
            <div class="flex flex-col">
              <div class="flex items-center gap-2">
                <h1 class="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                  <span class="text-blue-600">InvestBuddy</span><span class="text-green-600">.ai</span>
                </h1>
              </div>
            </div>
          </RouterLink>
        </div>

        <!-- Right: Help, User menu + Logout -->
        <div class="flex items-center space-x-3">
          <!-- Help Center -->
          <button
            @click="showHelpCenter = true"
            class="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all hover:shadow-sm"
            title="Help Center"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="hidden xl:inline">Help</span>
          </button>

          <span class="hidden md:block text-sm text-gray-700">
            Welcome, {{ authStore.user?.name || 'User' }}!
          </span>
          <button
            @click="handleLogout"
            class="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-red-700 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>
    </nav>

    <!-- Sidebar Navigation -->
    <AppSidebar v-if="authStore.isAuthenticated && shouldShowLayout" />

    <!-- Main Content with dynamic padding based on sidebar state -->
    <main 
      :class="[
        shouldShowLayout && authStore.isAuthenticated 
          ? 'pt-16 transition-all duration-300' 
          : '',
        contentOffsetClass
      ]"
      class="relative z-0 flex-1"
    >
      <div :class="shouldShowLayout && authStore.isAuthenticated ? 'w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8' : ''">
        <RouterView />
      </div>
    </main>
    
    <!-- Footer - Show on all pages except auth flows -->
    <AppFooter 
      v-if="shouldShowFooter"
      :class="[
        'transition-all duration-300',
        contentOffsetClass
      ]"
    />

    <!-- Smart Market Data Status - Only show when authenticated and not closed -->
    <div 
      v-if="shouldShowLayout && authStore.isAuthenticated && showMarketStatus" 
      class="fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg max-w-sm z-50"
      :class="marketDataStatus.connected ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-blue-100 border border-blue-400 text-blue-700'"
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <div 
            class="w-3 h-3 rounded-full mr-2"
            :class="marketDataStatus.connected ? 'bg-green-500' : 'animate-pulse bg-blue-500'"
          ></div>
          <span class="text-sm">{{ marketDataStatus.message }}</span>
        </div>
        <button 
          @click="closeMarketStatus"
          class="ml-3 text-gray-500 hover:text-gray-700 transition-colors p-1"
          :class="marketDataStatus.connected ? 'hover:text-green-800' : 'hover:text-blue-800'"
          title="Close market status panel"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Small toggle button to reopen market status when closed -->
    <button
      v-if="shouldShowLayout && authStore.isAuthenticated && !showMarketStatus"
      @click="openMarketStatus"
      class="fixed bottom-4 right-4 w-10 h-10 rounded-full shadow-lg bg-white border border-gray-300 hover:border-gray-400 transition-colors flex items-center justify-center z-50"
      title="Show market status panel"
    >
      <div 
        class="w-3 h-3 rounded-full"
        :class="marketDataStatus.connected ? 'bg-green-500' : 'animate-pulse bg-blue-500'"
      ></div>
    </button>

    <!-- Toast Notifications -->
    <ToastContainer />

    <!-- Price Alert Notifications -->
    <AlertNotification
      v-for="alert in activeAlerts"
      :key="alert.alert.id"
      :triggered-alert="alert"
      @dismiss="dismissAlert(alert.alert.id)"
      @buy="handleAlertBuy"
      @sell="handleAlertSell"
    />

    <!-- Cookie Consent Banner (only on login/main pages, not during verification) -->
    <CookieConsent v-if="shouldShowCookieConsent" />

    <!-- Help Center Modal -->
    <HelpCenter 
      v-if="authStore.isAuthenticated && shouldShowLayout"
      v-model="showHelpCenter"
    />
  </div>
</template>

<script setup lang="ts">
import { RouterView, RouterLink } from 'vue-router'
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from './stores/auth'
import { useSidebarStore } from './stores/sidebar'
import { wsService } from './services/websocket'
import { MarketHours } from './utils/marketHours'
import { useWebSocketInit } from './composables/useWebSocketInit'
import ToastContainer from './components/ui/ToastContainer.vue'
import AlertNotification from './components/AlertNotification.vue'
import CookieConsent from './components/CookieConsent.vue'
import AppFooter from './components/AppFooter.vue'
import HelpCenter from './components/HelpCenter.vue'
import AppSidebar from './components/AppSidebar.vue'
import { cookieService } from './services/cookieService'
import apiService from './services/api'
import { logger } from './utils/logger'
import type { TriggeredAlert } from '../../shared/src/types'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const sidebarStore = useSidebarStore()
const isConnected = ref(false)

// Initialize WebSocket monitoring
const { checkAndConnect } = useWebSocketInit()

// Check if current route should hide layout
const shouldShowLayout = computed(() => {
  const hideLayoutRoutes = ['/', '/login', '/register', '/verify-email', '/verify-phone', '/auth/callback', '/forgot-password', '/reset-password']
  return !hideLayoutRoutes.includes(route.path)
})

// Check if footer should be shown (show on public pages too)
const shouldShowFooter = computed(() => {
  const hideFooterRoutes = ['/login', '/register', '/verify-email', '/verify-phone', '/auth/callback', '/forgot-password', '/reset-password']
  return !hideFooterRoutes.includes(route.path)
})

const contentOffsetClass = computed(() => {
  if (!shouldShowLayout.value || !authStore.isAuthenticated) {
    return ''
  }
  return sidebarStore.isCollapsed ? 'lg:ml-16' : 'lg:ml-64'
})

// Check if cookie consent should be shown (hide during verification flows)
const shouldShowCookieConsent = computed(() => {
  const hideConsentRoutes = ['/verify-email', '/verify-phone', '/auth/callback', '/register']
  return !hideConsentRoutes.includes(route.path)
})

// Help modal
const showHelpCenter = ref(false)

// Market status panel visibility - remember user preference
const showMarketStatus = ref(localStorage.getItem('showMarketStatusPanel') !== 'false')

// Price alert notifications
const activeAlerts = ref<TriggeredAlert[]>([])

// Smart market data status
const marketDataStatus = computed(() => {
  const isMarketOpen = MarketHours.isMarketOpen()
  const statusString = MarketHours.getMarketStatusString()
  
  if (isMarketOpen) {
    return {
      connected: true,
      message: `${statusString} - Real-time WebSocket data`
    }
  } else {
    return {
      connected: false,
      message: statusString
    }
  }
})

onMounted(async () => {
  // Initialize authentication
  authStore.initialize()
})

// Watch for authentication changes
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth && !wsService.isConnected()) {
    setupWebSocket()
  } else if (!isAuth && wsService.isConnected()) {
    wsService.disconnect()
    isConnected.value = false
  }
}, { immediate: true })

function setupWebSocket() {
  // Set up WebSocket connection status
  isConnected.value = wsService.isConnected()
  
  wsService.onConnectionStatusChange((connected) => {
    isConnected.value = connected
    
    // Authenticate when connected
    if (connected && authStore.user?.id) {
      wsService.emit('auth', { userId: authStore.user.id })
      
      // Register price alert listener AFTER connection is established
      // This ensures it's not removed by socket.removeAllListeners()
      wsService.on('price-alert-triggered', (data: TriggeredAlert) => {
        activeAlerts.value.push(data)
        
        // Browser notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Price Alert Triggered', {
            body: `${data.alert.symbol} reached $${data.currentPrice.toFixed(2)}`,
            icon: '/favicon.ico'
          })
        }
      })
    } else if (connected) {
      console.warn('⚠️ WebSocket connected but no user ID available')
    }
  })
  
  // Small delay to ensure authentication is fully loaded
  setTimeout(() => {
    // Connect only if backend is available AND market is open
    fetch(import.meta.env.VITE_API_URL + '/health')
      .then(response => {
        if (response.ok) {
          logger.debug('Backend available, checking market status...')
          const isMarketOpen = MarketHours.isMarketOpen()
          
          if (isMarketOpen) {
            logger.debug('Market is OPEN - connecting WebSocket for real-time updates')
            wsService.connect()
            
            // Authenticate if already connected (in case of reconnection)
            if (wsService.isConnected() && authStore.user?.id) {
              wsService.emit('auth', { userId: authStore.user.id })
            }
          } else {
            logger.debug('Market is CLOSED - WebSocket will connect when market opens')
            // Use checkAndConnect to set up monitoring
            checkAndConnect()
          }
        }
      })
      .catch(() => {
        console.warn('📡 Backend not available, WebSocket will remain disconnected')
      })
  }, 500)
}

function dismissAlert(alertId: string) {
  activeAlerts.value = activeAlerts.value.filter(a => a.alert.id !== alertId)
}

function handleAlertBuy(symbol: string, price: number) {
  const alert = activeAlerts.value.find(a => a.alert.symbol === symbol)
  
  // Navigate to portfolio page with buy intent
  router.push({
    path: '/portfolio',
    query: { 
      action: 'buy',
      symbol: symbol,
      symbolName: alert?.alert.symbolName || '',
      price: price.toFixed(2)
    }
  })
}

function handleAlertSell(symbol: string) {
  const alert = activeAlerts.value.find(a => a.alert.symbol === symbol)
  
  // Navigate to portfolio page with sell intent
  router.push({
    path: '/portfolio',
    query: { 
      action: 'sell',
      symbol: symbol,
      symbolName: alert?.alert.symbolName || ''
    }
  })
}

async function handleLogout() {
  await authStore.logout()
  router.push('/')  // Redirect to home landing page instead of login
}

function closeMarketStatus() {
  showMarketStatus.value = false
  localStorage.setItem('showMarketStatusPanel', 'false')
}

function openMarketStatus() {
  showMarketStatus.value = true
  localStorage.setItem('showMarketStatusPanel', 'true')
}

// Close mobile menu when route changes
watch(route, () => {
  // Close mobile sidebar menu
  sidebarStore.closeMobileMenu()
  
  // Track page views when route changes (if analytics enabled)
  if (cookieService.getPreferences().analytics) {
    cookieService.trackPageView()
  }
})
</script>

<style>
body {
  font-family: 'Inter', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
</style>